import { getEvmClient } from '@1delta/providers'
import type { Address } from 'viem'
import { apyToAprPercent } from '../../../utils/parsing'
import { YieldFetcher } from '../../types'
import { annualizeRateDeltaPercent } from '../annualizeRate'
import { fetchDefiLlamaApy } from './defillama'

const thBill = 'Theo Short Duration US Treasury Fund::THBILL'
/**
 * The Monad (143) row for the SAME address carries
 * `name() == symbol() == 'thBILL'` and so groups separately. A GROUP_ALIAS
 * folds it into the canonical key, but only from the next token-list regen —
 * emit both so the join holds across that transition either way. The stcUSD
 * lesson: one asset, one address, two spellings, and the chains carrying the
 * other spelling silently read 0 %.
 */
const thBillBare = 'thBILL::thBILL'

/**
 * Theo THBILL intrinsic APR.
 *
 * The former source (`thbill-api.theo.xyz/snapshots`) is now behind a
 * host-wide Cloudflare block — every path returns a 403 challenge page
 * server-side, so it can't be fetched (it was returning HTML that the JSON
 * parse choked on, leaving the value stale). THBILL's yield is instead read
 * from DefiLlama's `theo-network-thbill` pool (Ethereum deployment; the
 * Arbitrum / Hyperliquid pools report the same fund APY), converted
 * APY% → APR% for consistency with the rest of the intrinsic map.
 */
const THBILL_DEFILLAMA_POOL = 'e17dab50-85b1-45e8-b4a5-b8ae7898c4a3'

export const thbillFetcher: YieldFetcher = {
  label: 'THBILL',
  fetch: async () => {
    const apy = await fetchDefiLlamaApy(THBILL_DEFILLAMA_POOL)
    const apr = apyToAprPercent(apy)
    return { [thBill]: apr, [thBillBare]: apr }
  },
}

/**
 * Theo **sthUSD** — intrinsic APR on a share price the issuer PUSHES.
 *
 * sthUSD is the ERC-4626 staked wrapper over `thUSD`, Theo's gold-yield
 * stablecoin (gold leasing + CME futures roll). Ethereum
 * `0xa808bc97…` is the only vault; Stable (988), Arbitrum, BNB and Mantle
 * carry the same LayerZero OFT address `0xd1db2090…`, and **97 % of the
 * supply sits on Stable**, where it is the largest Morpho Blue collateral.
 * One rate covers every deployment — they are the same shares.
 *
 * **There is no API and no DefiLlama pool.** Theo's whole host is behind a
 * Cloudflare block that returns 403 to a server-side fetch on every path
 * (`docs.theo.xyz`, `api.theo.xyz`, `thbill-api.theo.xyz` — the same block
 * `thbillFetcher` above already had to route around), and DefiLlama carries
 * `theo-network-thbill` but nothing for thUSD; its `morpho-blue Stable
 * STHUSD` pool reports `apy: 0`. So the share price itself is the only
 * source, and this fetcher is the on-chain rate-delta shape (stCELO, wstGBP,
 * stCORE) with no primary to fall back from.
 *
 * **The accrual is a STEP FUNCTION, and a short window is noise.** Yield is
 * not streamed: a `yieldDistributor` calls `setYield(amount)`, the batch
 * vests over `vestingDuration` (10,800 s = 3 h), and nothing accrues until
 * the next push — `setYield` even reverts while `getUnvestedAmount() > 0`,
 * so a batch and its successor cannot overlap. Measured daily on-chain over
 * the 45 days to 2026-08-25 the price moves in steps of at most +142e-6/day
 * (≈ 5.10 % APR) separated by flat stretches of up to **nine days**, and it
 * has been flat since 2026-08-19. A 7-day window therefore reads 0.17 %
 * where a 30-day window reads 0.85 % and the vault's whole life reads
 * 4.12 % — three answers to the same question, and only the length of the
 * window separates them.
 *
 * `WINDOW_SECONDS` is **30 days** as the compromise: long enough that a
 * single plateau cannot dominate it, short enough that it still reports what
 * this vault is paying NOW rather than what it paid in May. It is a REALIZED
 * trailing rate, not a forward one, and the two currently disagree by 6× —
 * the vault ran a steady ~5.1 % until 2026-07-21 and has decayed since, while
 * Theo's marketing quotes 6.5–11 % APY on locked vault products that are not
 * this row. Publish what was paid.
 *
 * Needs an archival Ethereum RPC and is bounded exactly like `wrenFetcher` —
 * two endpoint tries and a hard 12 s ceiling — because this runs on a path
 * `fetchSavingsVaults` awaits, and a slow rotation is a latency bug for every
 * caller. On failure the row reports 0 rather than a fabricated rate.
 */

/** Ethereum sthUSD vault — the only deployment that is a vault. */
const STHUSD_VAULT: Address = '0xa808bc9775cb41c52c7842f8b50427fe7a770326'
/** 6-decimal shares over 6-decimal thUSD — NOT the usual 18. */
const STHUSD_UNIT = 10n ** 6n

/** Trailing window. See the step-function note above before shortening it. */
const STHUSD_WINDOW_SECONDS = 30 * 24 * 60 * 60
const ETH_BLOCK_TIME_SECONDS = 12
const STHUSD_WINDOW_BLOCKS = BigInt(
  Math.floor(STHUSD_WINDOW_SECONDS / ETH_BLOCK_TIME_SECONDS),
)
/** Small on purpose — this is on an awaited path. Same reasoning as Wren's. */
const STHUSD_MAX_RPC_TRIES = 2
const STHUSD_DEADLINE_MS = 12_000

export const STHUSD_KEY = 'STHUSD'
/**
 * Token-list assetGroups. Ethereum stores `Staked thUSD::STHUSD`; the Stable
 * (988) OFT answers `name() == symbol() == 'sthUSD'`, so an unaliased list
 * would key it `sthUSD::sthUSD` — the Cap stcUSD split exactly, where one
 * asset at one address across three chains lost its intrinsic yield on the
 * two that carried the other spelling. Emit BOTH so the join survives the
 * publish transition in either direction.
 */
export const STHUSD_GROUP_KEY = 'Staked thUSD::STHUSD'
export const STHUSD_GROUP_KEY_OFT = 'sthUSD::sthUSD'

const CONVERT_TO_ASSETS_ABI = [
  {
    name: 'convertToAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256' }],
    outputs: [{ type: 'uint256' }],
  },
] as const

const readSthUsdRateAt = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: any,
  blockNumber?: bigint,
): Promise<bigint> =>
  client.readContract({
    address: STHUSD_VAULT,
    abi: CONVERT_TO_ASSETS_ABI,
    functionName: 'convertToAssets',
    args: [STHUSD_UNIT],
    ...(blockNumber !== undefined ? { blockNumber } : {}),
  }) as Promise<bigint>

/** Trailing realized APR percent off the vault's own share price. */
const computeSthUsdApr = async (): Promise<number> => {
  let lastErr: unknown
  const deadline = Date.now() + STHUSD_DEADLINE_MS
  for (let rpcId = 0; rpcId < STHUSD_MAX_RPC_TRIES; rpcId++) {
    if (Date.now() > deadline) break
    try {
      const client = getEvmClient('1', rpcId)
      const head = await client.getBlockNumber()
      const pastBlock =
        head > STHUSD_WINDOW_BLOCKS ? head - STHUSD_WINDOW_BLOCKS : 0n

      const [rateNow, rateThen, headBlock, thenBlock] = await Promise.all([
        readSthUsdRateAt(client),
        readSthUsdRateAt(client, pastBlock),
        client.getBlock({ blockNumber: head }),
        client.getBlock({ blockNumber: pastBlock }),
      ])

      // The vault launched at exactly par and the share price only accrues
      // (`setYield` adds assets; nothing removes them — a redemption burns
      // shares against assets snapshotted at request time). A read below par,
      // or a decrease, is a pruned node answering old state as zero — never a
      // loss to report.
      if (rateNow < STHUSD_UNIT || rateThen < STHUSD_UNIT) {
        throw new Error('sthUSD: share price below par — pruned or bad read')
      }
      if (rateNow < rateThen) {
        throw new Error('sthUSD: share price decreased — non-archival RPC')
      }

      const elapsed = Number(headBlock.timestamp - thenBlock.timestamp)
      if (elapsed <= 0) throw new Error('sthUSD: non-positive window')

      return annualizeRateDeltaPercent(rateNow, rateThen, elapsed)
    } catch (e) {
      lastErr = e
    }
  }
  throw lastErr ?? new Error('sthUSD: no RPC served archive state')
}

export const sthusdFetcher: YieldFetcher = {
  label: 'STHUSD',
  fetch: async () => {
    const apr = await computeSthUsdApr().catch(() => 0)
    return {
      [STHUSD_KEY]: apr,
      [STHUSD_GROUP_KEY]: apr,
      [STHUSD_GROUP_KEY_OFT]: apr,
    }
  },
}
